import { Component, ElementRef, ViewChild, Input, SimpleChanges, OnChanges, OnDestroy, Output, EventEmitter } from '@angular/core';
import { formatDate, formatNumber } from '@angular/common';
import { ApexOptions } from 'apexcharts';
import { PriceDataSeries } from 'src/app/models/price-chart';
import { environment } from 'src/environments/environment';
import { ChartColor } from 'src/app/models/chart';

@Component({
  selector: 'price-chart',
  templateUrl: './price-chart.component.html',
  styleUrls: ['./price-chart.component.scss']
})
export class PriceChartComponent implements OnChanges, OnDestroy {
  @Input() options: ApexOptions;
  @Input() color: ChartColor = ChartColor.Positive;
  @Input() data: PriceDataSeries;
  @Output() timeLineChange = new EventEmitter<{ start: number }>();

  @ViewChild('chart', { static: true }) chartElement: ElementRef;

  loading = true;
  chart: ApexCharts;

  useGlobalCurrency = !!environment.currencies.globalCurrency;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.data?.currentValue && changes?.data?.currentValue !== changes?.data?.previousValue) {
      this.loading = false;
      this.renderChart();
    }

    if (!changes?.data?.currentValue && changes?.color?.currentValue && changes?.color?.currentValue !== changes?.color?.previousValue) {
      this.updateChartColor();
    }
  }

  ngOnDestroy(): void {
    this.destroyChart();
  }

  updateChartColor() {
    if (!this.chart) {
      return;
    }

    this.chart?.updateOptions({
      colors: [this?.color],
    });
  }

  destroyChart() {
    this.chart?.destroy();
  }

  renderChart() {
    if (this.chart) {
      this.updateChart();
    } else {
      this.createChart();
    }
  }

  updateChart() {
    if (!this.chart || this.data?.defaultCurrencyData?.data?.length === 0) {
      return;
    }

    this.chart?.updateOptions({
      series: [{
        data: this.data?.defaultCurrencyData?.data
      }],
      colors: [this?.color],
    });
  }

  createChart() {
    if (this.chart) {
      this.chart.destroy();
    }

    if (!this.chartElement.nativeElement) {
      return;
    }

    this.chart = new ApexCharts(
      this.chartElement.nativeElement,
      this.options ? this.options : this.getChartOptions()
    );

    this.chart.render();
  }

  getChartOptions(): ApexOptions {
    return {
      chart: {
        parentHeightOffset: 0,
        height: '100%',
        type: 'line',
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        animations: {
          enabled: true,
          easing: 'easein',
          animateGradually: {
            enabled: true
          },
          dynamicAnimation: {
            enabled: true
          }
        }
      },
      colors: [this.color],
      series: [{
        name: 'Chart',
        data: this.data?.defaultCurrencyData?.data,
      }],
      xaxis: {
        type: 'datetime',
        tooltip: {
          enabled: false
        },
        axisBorder: {
          color: '#DDD'
        },
        axisTicks: {
          color: '#DDD'
        },
        labels: {
          datetimeUTC: false,
          style: {
            colors: ['#DDD']
          }
        }
      },
      yaxis: {
        labels: {
          formatter: (val) => {
            return `${this.useGlobalCurrency ? '$' : this.data?.baseCurrencyData?.currency + ' '}${formatNumber(val, 'en', '0.2-6')}`;
          },
          offsetX: 10,
          padding: 12,
          style: {
            colors: '#DDD'
          }
        },
      },
      grid: {
        borderColor: '#EEE'
      },
      stroke: {
        curve: 'smooth',
        width: 2
      },
      tooltip: {
        enabled: true,
        custom: ({seriesIndex, dataPointIndex, w}) => {
          const point = w.config.series[seriesIndex].data[dataPointIndex];
          const x = formatDate(point.x, 'MMM dd - HH:mm', 'en');
          const y1 = formatNumber(this.data?.globalCurrencyData?.data[dataPointIndex]?.y, 'en', '0.2-6');
          const y2 = formatNumber(this.data?.baseCurrencyData?.data[dataPointIndex]?.y, 'en', '0.2-6');
          const globalPrice = `${this.data?.globalCurrencyData?.currency} ${y1}`;
          const basePrice = `${this.data?.baseCurrencyData?.currency} ${y2}`;
          return `<div class="apexchart-tooltip">
            <div class="y">${this.useGlobalCurrency ? globalPrice : basePrice}</div>
            ${this.useGlobalCurrency ? `<div class="y text-sm text-gray">${basePrice}</div>` : ''}
            <div class="x">${x}</div>
          <div>`;
        }
      }
    };
  }
}
